import {
  AuthError,
  PublicClientApplication,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';

declare module '#app' {
  interface NuxtApp {
    $msal: PublicClientApplication;
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $msal: PublicClientApplication;
  }
}

export default defineNuxtPlugin(async (nuxtApp) => {
  // MSAL v5 silent renewal loads the redirectUri in a hidden iframe and waits for the response to be broadcast back
  if (window.parent !== window) {
    try {
      await broadcastResponseToMainFrame();
      // MSAL removes the iframe once it has the response, so the app is never booted inside it
      return new Promise<never>(() => {});
    } catch {
      // No auth response in the URL: not MSAL's iframe, boot as usual
    }
  }

  const config = useRuntimeConfig();
  const clientId = config.public.entraClientId as string;
  const tenantId = config.public.entraTenantId as string;

  const msalConfig: Configuration = {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: `${window.location.origin}/auth/callback`,
      postLogoutRedirectUri: `${window.location.origin}/auth/login`,
    },
    cache: {
      cacheLocation: 'localStorage',
    },
  };

  const msal = new PublicClientApplication(msalConfig);

  await msal.initialize();

  const authError = useState<string | null>('auth.error', () => null);

  try {
    const result = (await msal.handleRedirectPromise()) as AuthenticationResult | null;
    if (result?.account) {
      msal.setActiveAccount(result.account);
    } else {
      const existing = msal.getAllAccounts()[0];
      if (existing) {
        msal.setActiveAccount(existing);
      }
    }
  } catch (e) {
    if (e instanceof AuthError) {
      console.error('[msal] handleRedirectPromise AuthError:', {
        errorCode: e.errorCode,
        errorMessage: e.errorMessage,
        subError: (e as AuthError & { subError?: string }).subError,
        correlationId: e.correlationId,
      });
    } else {
      console.error('[msal] handleRedirectPromise error:', e);
    }
    authError.value = 'generic';
  }

  nuxtApp.provide('msal', msal);
});
